import React, { useState } from "react"
import { Link, useRouteMatch } from "react-router-dom"
import { useQueryClient } from "react-query"
import {
  Box, Badge, Flex, ListItem, Text, Tooltip, UnorderedList,
} from "@chakra-ui/react"
import Icon from "components/elements/Icon"
import LanguageTemplatesIcon from "components/elements/LanguageTemplatesIcon"
import UpgradeGateModal from "components/elements/UpgradeGateModal"
import Button from "components/Buttons/Button"
import Menu, { MenuDivider, MenuItem } from "components/dialogs/Menu"
import ConfirmDelete from "components/dialogs/Confirm/ConfirmDelete"
import SettingsSection from "components/settings/SettingsSection"
import RoundedList from "pages/EventShow/Settings/RoundedList"
import { DioboxEvent, LanguageTemplate, Translations } from "sharedTypes"
import { useAccountConfiguration } from "queries/users"
import { useEvents } from "queries/events"
import { useForms } from "queries/forms"
import useCurrentAccount from "services/useCurrentAccount"
import { languageTemplatesCacheKey, useLanguageTemplates } from "queries/languageTemplates"
import {
  postLanguageTemplate, patchLanguageTemplate, deleteLanguageTemplate, importLanguageTemplate,
} from "api/languageTemplates"
import { successToast } from "utilities/toasts"
import TemplateNameModal, { BLANK_TEMPLATE_ID } from "./TemplateNameModal"
import CopyTemplateModal from "./CopyTemplateModal"

type Props = {
  event: DioboxEvent
  updateEvent: (params: {}) => Promise<void>
}

const LanguageTemplatesList = ({ event, updateEvent }: Props) => {
  const { url } = useRouteMatch()
  const queryClient = useQueryClient()
  const { accountConfiguration } = useAccountConfiguration()
  const { data: templates = [] } = useLanguageTemplates(event.id)
  const fieldKeys = Object.keys(accountConfiguration.translations ?? {})
  // The sparse-override model only ever stores keys someone genuinely interacted with —
  // untouched fields are never included in the saved object at all. So a key being present
  // means "customized," even if its value is an explicitly-cleared empty string: that empty
  // string is a literal override sent to guests, not a fallback to the English default.
  // Nothing is ever truly "missing" — unset fields fall back to English via the backend's
  // deep-merge — but Complete/N-missing reads clearer to organizers than a bare fraction.
  // Keys a template still stores for a removed field don't count.
  const missingFieldsCount = (template: LanguageTemplate) => (
    fieldKeys.filter((key) => !(key in template.translations)).length
  )
  const currentAccount = useCurrentAccount()
  const { data: events = [] } = useEvents(currentAccount.id)
  const hasOtherEvents = events.some((otherEvent) => otherEvent.id !== event.id)
  const { customLanguageTemplatesEnabled } = event
  const { data: formsData } = useForms(event.id)
  const forms = formsData?.data ?? []

  const [duplicateModal, setDuplicateModal] = useState<{ baseTemplateId?: string } | null>(null)
  const [showCopyModal, setShowCopyModal] = useState(false)
  const [renamingTemplate, setRenamingTemplate] = useState<LanguageTemplate | null>(null)

  const refetch = () => queryClient.invalidateQueries(languageTemplatesCacheKey(event.id))

  // Shared/offered templates (English/French/Spanish) already have a real id — Set as
  // Default just points the event at it directly, same as any other template. No copy.
  const handleSetDefault = async (template: LanguageTemplate) => {
    await updateEvent({ defaultLanguageTemplateId: template.id })
    successToast({ title: "Default Language Template updated" })
  }

  const handleDelete = async (template: LanguageTemplate) => {
    await deleteLanguageTemplate(event.id, template.id)
    refetch()
  }

  const handleDuplicateSubmit = async (
    { name, baseTemplateId }: { name: string, baseTemplateId?: string },
  ) => {
    const base = templates.find((t) => t.id === baseTemplateId)
    // A blank template is genuinely empty — not every field pre-set to "" — so it inherits the
    // English default (via the backend's deep-merge fallback) for anything not customized,
    // rather than showing blank text everywhere until every one of the ~76 fields is filled in.
    const translations = baseTemplateId === BLANK_TEMPLATE_ID
      ? {} as Translations
      : (base?.translations ?? {} as Translations)

    await postLanguageTemplate(event.id, { name, translations })
    refetch()
    setDuplicateModal(null)
  }

  const handleRename = async ({ name }: { name: string }) => {
    if (!renamingTemplate) {
      return
    }

    await patchLanguageTemplate(event.id, renamingTemplate.id, { name })
    refetch()
    setRenamingTemplate(null)
  }

  const handleImport = async (sourceEventId: string, sourceLanguageTemplateId: string) => {
    await importLanguageTemplate(event.id, sourceEventId, sourceLanguageTemplateId)
    refetch()
  }

  const inUseReasons = (template: LanguageTemplate): string[] => {
    const reasons: string[] = []

    if (template.id === event.defaultLanguageTemplateId) {
      reasons.push("it's the event's default Language Template")
    }

    const assignedForms = forms.filter((form) => form.languageTemplateId === template.id)
    if (assignedForms.length > 0) {
      const formNames = assignedForms.map((form) => form.name || form.externalId).join(", ")
      reasons.push(`it's assigned to: ${formNames}`)
    }

    return reasons
  }

  const deleteMenuItem = (template: LanguageTemplate) => {
    const reasons = inUseReasons(template)

    const menuItem = (
      <MenuItem onClick={() => handleDelete(template)} isDisabled={reasons.length > 0} color="red.700">
        Delete
      </MenuItem>
    )

    if (reasons.length === 0) {
      return (
        <ConfirmDelete type="Language Template" name={template.name}>
          {menuItem}
        </ConfirmDelete>
      )
    }

    return (
      <Box w="100%" sx={{ "& > span": { display: "block", width: "100%" } }}>
        <Tooltip label={`Can't delete: ${reasons.join(" and ")}.`} shouldWrapChildren>
          {menuItem}
        </Tooltip>
      </Box>
    )
  }

  // An event with no explicit defaultLanguageTemplateId still resolves to English everywhere
  // (the backend's resolver, and every other reference to "the default" in this app, already
  // fall back to the string "English") — so the shared English row itself reads as the default
  // here too, rather than the list showing no default at all when nothing's been chosen yet.
  const isDefaultTemplate = (template: LanguageTemplate) => (
    template.id === event.defaultLanguageTemplateId
    || (!event.defaultLanguageTemplateId && template.isOffered && template.name === "English")
  )

  // Duplicating any existing template (shared or custom) creates a new custom template,
  // same as "New Template" — this is the second path to that action, so it needs the same
  // Corporate gate, not just the top-level button.
  const duplicateMenuItem = (template: LanguageTemplate) => (
    customLanguageTemplatesEnabled ? (
      <MenuItem onClick={() => setDuplicateModal({ baseTemplateId: template.id })}>
        Duplicate
      </MenuItem>
    ) : (
      <UpgradeGateModal
        icon={LanguageTemplatesIcon}
        title="Custom Language Templates"
        description="Upgrade to create custom Language Templates for your events."
      >
        <MenuItem>Duplicate</MenuItem>
      </UpgradeGateModal>
    )
  )

  const rowMenuItems = (template: LanguageTemplate) => {
    const isDefault = isDefaultTemplate(template)

    const setDefaultItem = !isDefault && (
      <MenuItem onClick={() => handleSetDefault(template)}>Set as Default</MenuItem>
    )

    if (template.isOffered) {
      return [
        <MenuItem as={Link} to={`${url}/${template.id}/edit`}>View</MenuItem>,
        setDefaultItem,
        duplicateMenuItem(template),
      ].filter(Boolean)
    }

    return [
      <MenuItem as={Link} to={`${url}/${template.id}/edit`}>Edit</MenuItem>,
      setDefaultItem,
      duplicateMenuItem(template),
      <MenuItem onClick={() => setRenamingTemplate(template)}>Rename</MenuItem>,
      <MenuDivider />,
      deleteMenuItem(template),
    ].filter(Boolean)
  }

  const statusCell = (template: LanguageTemplate) => {
    const missing = missingFieldsCount(template)

    if (missing === 0) {
      return (
        <Text w="30%" color="green.400" fontWeight="semibold">
          <Icon icon="checkmark" mr={1} />Complete
        </Text>
      )
    }

    return (
      <Text w="30%" color="red.400" fontWeight="semibold">
        <Icon icon="times" mr={1} />{missing} field{missing === 1 ? "" : "s"} missing
      </Text>
    )
  }

  const templateRow = (template: LanguageTemplate) => (
    <Flex px={6} py={3} justify="space-between" align="center" key={template.id}>
      <Text w="40%" fontSize="lg">
        {template.name}
        {isDefaultTemplate(template) && <Badge ml={2}>Default</Badge>}
      </Text>
      <Text w="30%">{template.isOffered ? "Diobox" : "Custom"}</Text>
      {statusCell(template)}
      <Menu buttonText="Options" menuItems={rowMenuItems(template)} noPadding />
    </Flex>
  )

  const anyTemplateMissingFields = templates.some((template) => missingFieldsCount(template) > 0)

  return (
    <SettingsSection
      title="Language Templates"
      description={(
        <>
          <Text>
            Language Templates enable you customize the terminology and messages guests see
            throughout your event. You can:
          </Text>
          <UnorderedList pl={5} spacing={1}>
            <ListItem>
              Adapt guest-facing text to match your event&apos;s branding and voice.
            </ListItem>
            <ListItem>
              Create a template for a language Diobox doesn&apos;t currently support.
            </ListItem>
            <ListItem>
              Assign different language templates to individual registration forms.
            </ListItem>
          </UnorderedList>
        </>
      )}
      actionButtons={[
        customLanguageTemplatesEnabled ? (
          <Menu
            button={<Button leftIcon="plus">New Template</Button>}
            menuItems={[
              <MenuItem onClick={() => setDuplicateModal({})}>Duplicate a Template</MenuItem>,
              <Box w="100%" sx={{ "& > span": { display: "block", width: "100%" } }}>
                <Tooltip
                  label="You need at least one other event to copy a Language Template from."
                  isDisabled={hasOtherEvents}
                  shouldWrapChildren
                >
                  <MenuItem
                    isDisabled={!hasOtherEvents}
                    onClick={() => setShowCopyModal(true)}
                  >
                    Copy from Another Event
                  </MenuItem>
                </Tooltip>
              </Box>,
            ]}
          />
        ) : (
          <UpgradeGateModal
            icon={LanguageTemplatesIcon}
            title="Custom Language Templates"
            description="Upgrade to create custom Language Templates for your events."
          >
            <Button leftIcon="plus">New Template</Button>
          </UpgradeGateModal>
        ),
      ]}
    >
      <RoundedList>
        <Flex px={6} py={3} justify="space-between" align="center">
          <Text fontWeight="semibold" w="40%">LANGUAGE</Text>
          <Text fontWeight="semibold" w="30%">TYPE</Text>
          <Text fontWeight="semibold" w="30%">STATUS</Text>
          <Box w={24} />
        </Flex>
        {templates.map(templateRow)}
      </RoundedList>

      {anyTemplateMissingFields && (
        <Box borderRadius="xl" px={6} py={3} bgColor="orange.50" borderWidth={1} fontSize="lg">
          <Text>Missing fields will be shown to guests in English.</Text>
        </Box>
      )}

      {duplicateModal && (
        <TemplateNameModal
          mode="create"
          templates={templates}
          initialBaseTemplateId={duplicateModal.baseTemplateId}
          onClose={() => setDuplicateModal(null)}
          onSubmit={handleDuplicateSubmit}
        />
      )}

      {renamingTemplate && (
        <TemplateNameModal
          mode="rename"
          initialName={renamingTemplate.name}
          onClose={() => setRenamingTemplate(null)}
          onSubmit={handleRename}
        />
      )}

      {showCopyModal && (
        <CopyTemplateModal
          eventId={event.id}
          onClose={() => setShowCopyModal(false)}
          onCopy={handleImport}
        />
      )}
    </SettingsSection>
  )
}

export default LanguageTemplatesList
